import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { CalendarSettingModel, DayInMonth, ExportCalendarModel, SelectedCellModel } from './calendar.model';
import { DatepickerSettingModel } from './datepicker/datepicker.model';

const DBS_CALENDAR_CONFIG = {
  MAX_WEEK_INDEX: 6,
  MAX_DAY_INDEX: 7,
};
@Component({
  selector: 'dbs-app-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss'],
})
export class CalendarComponent implements OnInit {
  title: string;
  currentDate: any;
  dayOfWeekTitles = ['日', '月', '火', '水', '木', '金', '土'];
  firstDayOfMonth: Date;
  optionsDMY: object;
  optionsMY: object;
  localeStr: string;
  localeJPStr: string;
  selectedCell: SelectedCellModel = { day: null, idxDay: -1, idxWeek: -1, isSetManual: false };
  daysInMonth: DayInMonth[] = [];
  inputDate: Date;
  titleDate = '';
  selectedDay = false;
  isDateInputView = true;
  japaneseName = '';
  yearJapan = '';
  month = '';
  date = '';
  yearNormal = '';
  isNotDate = false;
  jpErasFunc = new DatepickerSettingModel();
  isDisableDays = false;
  dateRangeEnd: Date;
  dateRangeStart: Date;
  hasDatas: string[];
  daysInMonths: { [yearMonth: string]: DayInMonth[] } = {};
  params = null;

  readonly calendarConfig = DBS_CALENDAR_CONFIG;

  constructor(
    public dialogRef: MatDialogRef<CalendarComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CalendarSettingModel,
  ) { }

  ngOnInit() {
    this.dateRangeEnd = this.data.dateRangeEnd;
    this.dateRangeStart = this.data.dateRangeStart;
    this.title = this.data.title;
    this.currentDate = this.data.currentDate || new Date();
    this.currentDate.setHours(0, 0, 0, 0);
    this.isNotDate = this.data.isNotDate ? true : false;
    this.setData();
    this.optionsDMY = this.data.optionsDMY;
    this.optionsMY = this.data.optionsMY;
    this.localeStr = this.data.localeStr;
    this.localeJPStr = this.data.localeJPStr;
    this.inputDate = this.currentDate;
    this.selectedCell.day = this.currentDate;
    this.isDateInputView = true;
    this.initCalendar();
  }

  setData() {
    if (this.currentDate === null || this.currentDate === 'Invalid Date' || isNaN(this.currentDate)) {
      this.currentDate = new Date();
    } else {
      const dateFormat = this.currentDate.toLocaleDateString('ja-JP-u-ca-japanese', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
      const yearArr = dateFormat.split('年');
      const yy = this.currentDate.getFullYear();
      this.yearNormal = yy.toString();
      const jpEra = this.jpErasFunc.findEraYear(this.currentDate);
      if (jpEra) {
        this.japaneseName = jpEra.name;
        const yearStr = Number(yearArr[0].replace(/^\D+/g, ''));
        const year = yearStr <= 9 ? '0' + yearStr : yearStr;
        this.yearJapan = year.toString();
        const day = this.currentDate.getDate() <= 9 ? '0' + this.currentDate.getDate() : this.currentDate.getDate();
        this.date = day.toString();
        const realMonth = this.currentDate.getMonth() + 1;
        const month = realMonth <= 9 ? '0' + realMonth : realMonth;
        this.month = month.toString();
      }
    }
  }

  changeMonth(dir = 'next', isDisable) {
    if (!isDisable) {
      const currentMonth = this.currentDate.getMonth();
      const nextDate = new Date(this.currentDate);
      const previousDate = new Date(this.currentDate);
      switch (dir) {
        case 'next':
          nextDate.setMonth(currentMonth + 1);
          if (this.dateRangeEnd && nextDate.getTime() > this.dateRangeEnd.getTime()) {
            this.currentDate = new Date(this.dateRangeEnd);
          } else {
            this.currentDate = nextDate;
          }
          break;
        case 'previous':
          previousDate.setMonth(currentMonth - 1);
          if (this.dateRangeStart && previousDate.getTime() < this.dateRangeStart.getTime()) {
            this.currentDate = new Date(this.dateRangeStart);
          } else {
            this.currentDate = previousDate;
          }
          break;
        default:
          break;
      }
      this.setData();
      this.initCalendar();
    }
  }

  changeYear(dir = 'next', isDisable) {
    if (!isDisable) {
      const currentYear = this.currentDate.getFullYear();
      const nextDate = new Date(this.currentDate);
      const previousDate = new Date(this.currentDate);

      switch (dir) {
        case 'next':
          nextDate.setFullYear(currentYear + 1);
          if (this.dateRangeEnd && nextDate.getTime() > this.dateRangeEnd.getTime()) {
            this.currentDate = new Date(this.dateRangeEnd);
          } else {
            this.currentDate = nextDate;
          }
          break;
        case 'previous':
          previousDate.setFullYear(currentYear - 1);
          if (this.dateRangeStart && previousDate.getTime() < this.dateRangeStart.getTime()) {
            this.currentDate = new Date(this.dateRangeStart);
          } else {
            this.currentDate = previousDate;
          }
          break;
        default:
          break;
      }
      this.setData();
      this.initCalendar();
    }
  }

  initCalendar() {
    const year = this.currentDate.getFullYear();
    const month = this.currentDate.getMonth() + 1;
    const length = this.daysInMonth.length;
    let reuse = false;
    if (length > 0) {
      const firstElement = this.daysInMonth.find(day => day.isInBound);
      const curYear = firstElement.date.getFullYear();
      const curMonth = firstElement.date.getMonth() + 1;
      if (curYear === year && curMonth === month) {
        reuse = true;
      }
    }
    if (!reuse) {
      this.firstDayOfMonth = new Date(year, month - 1, 1);
      this.daysInMonth = [];
      const currentYear = this.currentDate.getFullYear();
      const currentMonth = this.currentDate.getMonth();
      this.selectedCell = { day: this.currentDate, idxDay: -1, idxWeek: -1, isSetManual: true };
      if (!this.daysInMonths[currentYear + '-' + currentMonth]) {
        const firstIdx = this.firstDayOfMonth.getDay();
        for (let idxOfWeek = 0; idxOfWeek < DBS_CALENDAR_CONFIG.MAX_WEEK_INDEX; idxOfWeek++) {
          for (let idxOfDay = 0; idxOfDay < DBS_CALENDAR_CONFIG.MAX_DAY_INDEX; idxOfDay++) {
            this.printDayInCalendar(currentYear, currentMonth, firstIdx, idxOfWeek, idxOfDay);
          }
        }

        this.daysInMonths[currentYear + '-' + currentMonth] = this.daysInMonth;
      } else {
        this.daysInMonth = this.daysInMonths[currentYear + '-' + currentMonth];
      }
    }
  }

  isNullOrEmpty(value: any): boolean {
    if (value === undefined || value === null) {
      return true;
    }
    if (value === '') {
      return true;
    }
    return false;
  }

  printDayInCalendar(
    currentYear: number,
    currentMonth: number,
    firstIdx: number,
    idxOfWeek: number,
    idxOfDay: number,
  ) {
    const lastDay = idxOfDay - firstIdx + 1;
    const dayInMonth = new DayInMonth();
    if (idxOfWeek === 0) {
      const nextDay = new Date(currentYear, currentMonth, lastDay);
      const nextDayNumber = nextDay.getDay();
      if (idxOfDay === nextDayNumber) {
        if (lastDay > 0) {
          dayInMonth.date = nextDay;
          dayInMonth.isInBound = true;
          dayInMonth.day = lastDay;
        } else {
          const preMonth = new Date(currentYear, currentMonth, 0);
          const dayOfLastMonth = preMonth.getDate();
          dayInMonth.date = new Date(preMonth.setDate(dayOfLastMonth + lastDay));
          dayInMonth.isInBound = false;
          dayInMonth.day = dayOfLastMonth + lastDay;
        }
      }
    } else if (idxOfWeek < DBS_CALENDAR_CONFIG.MAX_WEEK_INDEX) {
      const nextDay = new Date(currentYear, currentMonth, idxOfWeek * DBS_CALENDAR_CONFIG.MAX_DAY_INDEX + lastDay);
      const nextDayNumber = nextDay.getDay();
      if (idxOfDay === nextDayNumber) {
        dayInMonth.date = nextDay;
        if (nextDay.getMonth() === currentMonth) {
          dayInMonth.isInBound = true;
        } else {
          dayInMonth.isInBound = false;
        }
        dayInMonth.day = nextDay.getDate();
      }
    }

    let isDisDay = false;

    const iYear = dayInMonth.date.getFullYear();
    const iMonth = dayInMonth.date.getMonth();
    const iDate = dayInMonth.date.getDate();
    const iTime = dayInMonth.date.getTime();

    let sYear;
    let sMonth;
    let sDate;
    let sTime;
    let eYear;
    let eMonth;
    let eDate;
    let eTime;

    if (this.dateRangeStart !== undefined) {
      sYear = this.dateRangeStart.getFullYear();
      sMonth = this.dateRangeStart.getMonth();
      sDate = this.dateRangeStart.getDate();
      sTime = this.dateRangeStart.getTime();
    }
    if (this.dateRangeEnd !== undefined) {
      eYear = this.dateRangeEnd.getFullYear();
      eMonth = this.dateRangeEnd.getMonth();
      eDate = this.dateRangeEnd.getDate();
      eTime = this.dateRangeEnd.getTime();
    }
    if (this.dateRangeStart !== undefined) {
      if (this.dateRangeEnd === undefined) {
        isDisDay = iTime < sTime && !(sYear === iYear && sMonth === iMonth && sDate === iDate);
      } else {
        isDisDay =
          (iTime < sTime && !(sYear === iYear && sMonth === iMonth && sDate === iDate)) ||
          (iTime > eTime && !(eYear === iYear && eMonth === iMonth && eDate === iDate));
      }
    } else {
      if (this.dateRangeEnd !== undefined) {
        isDisDay = eTime < iTime && !(eYear === iYear && eMonth === iMonth && eDate === iDate);
      }
    }

    if (isDisDay) {
      dayInMonth.isDisDay = true;
    } else {
      if (this.dateRangeEnd !== undefined) {
        isDisDay = eTime < iTime && !(eYear === iYear && eMonth === iMonth && eDate === iDate);
      }
    }

    if (isDisDay) {
      dayInMonth.isDisDay = true;
    }

    this.daysInMonth.push(dayInMonth);
  }

  clickCell(idxWeek: number, idxDay: number) {
    if (this.isDateInputView) {
      this.selectedCell.day = new Date(this.daysInMonth[idxWeek * DBS_CALENDAR_CONFIG.MAX_DAY_INDEX + idxDay].date);
      this.selectedCell.idxWeek = idxWeek;
      this.selectedCell.idxDay = idxDay;
      try {
        this.currentDate = new Date(this.selectedCell.day);
        this.setData();
      } catch (e) {
        //
      }
    }
  }

  onConfirm(idxWeek: number, idxDay: number) {
    this.selectedCell.day = this.daysInMonth[idxWeek * DBS_CALENDAR_CONFIG.MAX_DAY_INDEX + idxDay].date;
    this.selectedCell.idxWeek = idxWeek;
    this.selectedCell.idxDay = idxDay;
    if (this.isDateInputView) {
      const resp = new ExportCalendarModel();
      resp.date = this.selectedCell.day;
      this.dialogRef.close(resp);
    }
  }

  onConfirmOK() {
    if (this.isDateInputView) {
      const resp = new ExportCalendarModel();
      resp.date = this.selectedCell.day;
      this.dialogRef.close(resp);
    }
  }

  onDismiss() {
    this.dialogRef.close();
  }

  setDate() {
    if (
      this.yearNormal &&
      this.month &&
      this.date &&
      this.yearNormal.length === 4 &&
      this.month !== '0' &&
      this.date !== '0'
    ) {
      const year = Number(this.yearNormal);
      const month = Number(this.month) - 1;
      const date = Number(this.date);
      const dateCurrent = new Date(year, month, date);
      this.currentDate = dateCurrent;
      this.setData();
    }
  }

  onClickToday() {
    const today = new Date(new Date().setHours(0, 0, 0, 0));
    this.currentDate = today;
    this.initCalendar();
    const date = this.daysInMonth.find(day => day.date.getTime() === today.getTime());
    if (date) {
      this.selectedCell.day = new Date(date.date);
      this.selectedCell.idxWeek = this.daysInMonth.length % DBS_CALENDAR_CONFIG.MAX_WEEK_INDEX;
      this.selectedCell.idxDay = this.daysInMonth.length % DBS_CALENDAR_CONFIG.MAX_DAY_INDEX;
      this.currentDate = new Date(this.selectedCell.day);
      this.setData();
    }
  }
}
